import { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { PageHeader, rupee } from "../components/shared";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Crown, Gift, Trash2, Upload, Package } from "lucide-react";

const CATEGORIES = ["Haircut", "Hair Wash", "Shave", "Hair Spa", "Keratin", "Hair Colour",
  "Facial", "Detan", "Cleanup", "Manicure", "Pedicure", "Waxing", "Other"];
const EMPTY = { name: "", kind: "membership", price: 0, discount_type: "percent", discount_value: 10, scope: "all", categories: [], branch_ids: [], status: "active" };

export default function Packages() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/packages").then((r) => setRows(r.data.filter((p) => p.status !== "inactive")));
  useEffect(() => { load(); }, []);

  const save = async () => {
    const payload = { ...form, price: Number(form.price), discount_value: Number(form.discount_value) };
    try {
      if (editId) await api.put(`/packages/${editId}`, payload);
      else await api.post("/packages", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const toggleCat = (c) => setForm((f) => ({ ...f, categories: f.categories.includes(c) ? f.categories.filter((x) => x !== c) : [...f.categories, c] }));

  return (
    <div>
      <PageHeader title="Packages & Memberships" subtitle="Prepaid packages and membership tiers that auto-apply per-service discounts at billing"
        actions={<Button onClick={() => { setForm(EMPTY); setEditId(null); setOpen(true); }} data-testid="add-package-btn" className="rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37]"><Plus size={16} className="mr-1" /> Add</Button>} />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {rows.map((p) => (
          <Card key={p.id} className="p-5 bg-card border-border" data-testid={`package-card-${p.id}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2">
                <div className="h-9 w-9 rounded-sm bg-primary/15 flex items-center justify-center text-primary">{p.kind === "membership" ? <Crown size={18} /> : <Gift size={18} />}</div>
                <div>
                  <p className="font-medium">{p.name}</p>
                  <p className="text-xs text-primary capitalize">{p.kind}</p>
                </div>
              </div>
              <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => { setForm({ ...EMPTY, ...p }); setEditId(p.id); setOpen(true); }} data-testid={`edit-package-${p.id}`}><Pencil size={14} /></Button>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
              <div><span className="text-muted-foreground text-xs">Price</span><p>{p.price ? rupee(p.price) : "Free"}</p></div>
              <div><span className="text-muted-foreground text-xs">Discount</span><p className="text-primary">{p.discount_type === "percent" ? `${p.discount_value}%` : rupee(p.discount_value)}</p></div>
            </div>
            <div className="mt-3 flex flex-wrap gap-1">
              {p.scope === "all" ? <Badge variant="outline">All services</Badge> : (p.categories || []).map((c) => <Badge key={c} variant="outline">{c}</Badge>)}
            </div>
          </Card>
        ))}
        {!rows.length && <p className="text-muted-foreground text-sm">No packages yet.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-border max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editId ? "Edit" : "Add"} Package / Membership</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Name</Label><Input data-testid="package-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-secondary mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Type</Label>
                <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                  <SelectTrigger className="bg-secondary mt-1" data-testid="package-kind"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="membership">Membership</SelectItem><SelectItem value="package">Package</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Prepaid Price (₹)</Label><Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="bg-secondary mt-1" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Discount Type</Label>
                <Select value={form.discount_type} onValueChange={(v) => setForm({ ...form, discount_type: v })}>
                  <SelectTrigger className="bg-secondary mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="percent">Percentage %</SelectItem><SelectItem value="amount">Fixed ₹</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Discount Value</Label><Input type="number" data-testid="package-discount" value={form.discount_value} onChange={(e) => setForm({ ...form, discount_value: e.target.value })} className="bg-secondary mt-1" /></div>
            </div>
            <div><Label className="text-xs">Applies To</Label>
              <Select value={form.scope} onValueChange={(v) => setForm({ ...form, scope: v })}>
                <SelectTrigger className="bg-secondary mt-1" data-testid="package-scope"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">All services</SelectItem><SelectItem value="category">Specific categories</SelectItem></SelectContent>
              </Select>
            </div>
            {form.scope === "category" && (
              <div className="flex flex-wrap gap-2">
                {CATEGORIES.map((c) => (
                  <button key={c} type="button" onClick={() => toggleCat(c)}
                    className={`text-xs px-3 py-1 rounded-full border ${form.categories.includes(c) ? "bg-primary text-primary-foreground border-primary" : "bg-secondary text-muted-foreground border-border"}`}>{c}</button>
                ))}
              </div>
            )}
          </div>
          <DialogFooter><Button onClick={save} data-testid="package-save" className="rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37]">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <ComboPackages />
    </div>
  );
}

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const imgUrl = (p) => (p ? `${BACKEND}${p}` : "");
const EMPTY_COMBO = { name: "", description: "", image: "", service_ids: [], offer_price: 0, duration: 0, gender: "women", show_in_public: true, status: "active" };

function ComboPackages() {
  const [rows, setRows] = useState([]);
  const [services, setServices] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY_COMBO);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = () => api.get("/combo-packages").then((r) => setRows(r.data || [])).catch(() => {});
  useEffect(() => {
    load();
    api.get("/services").then((r) => setServices((r.data || []).filter((s) => s.status !== "inactive"))).catch(() => {});
  }, []);

  const actualTotal = form.service_ids.reduce((a, id) => a + (Number(services.find((s) => s.id === id)?.price) || 0), 0);
  const toggleSvc = (id) => setForm((f) => ({ ...f, service_ids: f.service_ids.includes(id) ? f.service_ids.filter((x) => x !== id) : [...f.service_ids, id] }));
  const openCreate = () => { setForm(EMPTY_COMBO); setEditId(null); setFile(null); setPreview(""); setOpen(true); };
  const openEdit = (p) => {
    setForm({ name: p.name || "", description: p.description || "", image: p.image || "", service_ids: p.service_ids || (p.services || []).map((s) => s.id), offer_price: p.offer_price || 0, duration: p.duration || 0, gender: p.gender || "women", show_in_public: p.show_in_public !== false, status: p.status || "active" });
    setEditId(p.id); setFile(null); setPreview(p.image ? imgUrl(p.image) : ""); setOpen(true);
  };
  const onFile = (e) => { const f = e.target.files?.[0]; if (f) { setFile(f); setPreview(URL.createObjectURL(f)); } };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Package name is required");
    if (form.service_ids.length < 2) return toast.error("Select at least 2 services for a combo");
    if (!(Number(form.offer_price) > 0)) return toast.error("Enter a valid offer price");
    setBusy(true);
    try {
      const payload = { ...form, offer_price: Number(form.offer_price), duration: Number(form.duration) || 0 };
      let pid = editId;
      if (editId) await api.put(`/combo-packages/${editId}`, payload);
      else { const { data } = await api.post("/combo-packages", payload); pid = data.id; }
      if (file && pid) { const fd = new FormData(); fd.append("file", file); await api.post(`/combo-packages/${pid}/image`, fd, { headers: { "Content-Type": "multipart/form-data" } }); }
      toast.success("Package saved"); setOpen(false); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const toggleStatus = async (p) => {
    try {
      await api.put(`/combo-packages/${p.id}`, { name: p.name, description: p.description || "", image: p.image || "", service_ids: p.service_ids || (p.services || []).map((s) => s.id), offer_price: p.offer_price, duration: p.duration || 0, gender: p.gender || "women", show_in_public: p.show_in_public !== false, status: p.status === "active" ? "inactive" : "active" });
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const del = async () => {
    try { await api.delete(`/combo-packages/${confirmDel.id}`); toast.success("Package deleted"); setConfirmDel(null); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="mt-10" data-testid="combo-packages-section">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-display text-2xl flex items-center gap-2"><Package size={20} className="text-primary" /> Combo / Offer Packages</h2>
          <p className="text-xs text-muted-foreground">Bundle services at one offer price — shown on Public Booking and selectable at Billing/POS.</p>
        </div>
        <Button onClick={openCreate} data-testid="add-combo-package-btn" className="rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37]"><Plus size={16} className="mr-1" /> Create Package</Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {rows.map((p) => (
          <Card key={p.id} className="overflow-hidden bg-card border-border flex flex-col" data-testid={`combo-package-card-${p.id}`}>
            {p.image ? <img src={imgUrl(p.image)} alt={p.name} className="h-36 w-full object-cover" /> : <div className="h-36 w-full bg-secondary flex items-center justify-center text-muted-foreground"><Package size={28} /></div>}
            <div className="p-4 flex flex-col flex-1">
              <div className="flex items-start justify-between gap-2">
                <p className="font-medium">{p.name}</p>
                <div className="flex items-center gap-1">
                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(p)} data-testid={`edit-combo-${p.id}`}><Pencil size={14} /></Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => setConfirmDel(p)} data-testid={`delete-combo-${p.id}`}><Trash2 size={14} /></Button>
                </div>
              </div>
              {p.description && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{p.description}</p>}
              {(p.services || []).length > 0 && <p className="text-xs text-primary/80 mt-2" data-testid={`combo-services-${p.id}`}>{p.services.map((s) => s.name).join("  +  ")}</p>}
              <div className="mt-2 flex items-baseline gap-2 flex-wrap">
                {p.actual_price > p.offer_price && <span className="text-sm text-muted-foreground line-through">{rupee(p.actual_price)}</span>}
                <span className="text-primary text-xl font-semibold">{rupee(p.offer_price)}</span>
                {p.savings > 0 && <span className="text-[10px] font-semibold text-green-400 bg-green-500/10 border border-green-500/30 rounded-full px-2 py-0.5">Save {rupee(p.savings)}</span>}
              </div>
              {p.duration > 0 && <p className="text-xs text-muted-foreground mt-1">{p.duration} min</p>}
              <div className="flex-1" />
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <Badge variant="outline" className={p.show_in_public !== false ? "border-primary/50 text-primary" : "text-muted-foreground"}>{p.show_in_public !== false ? "Public: ON" : "Public: OFF"}</Badge>
                <button type="button" onClick={() => toggleStatus(p)} data-testid={`combo-status-${p.id}`}
                  className={`text-xs px-3 py-1 rounded-full border ${p.status === "active" ? "bg-green-500/15 text-green-400 border-green-500/50" : "bg-secondary text-muted-foreground border-border"}`}>{p.status === "active" ? "Active" : "Inactive"}</button>
              </div>
            </div>
          </Card>
        ))}
        {!rows.length && <p className="text-muted-foreground text-sm">No combo packages yet. Click "Create Package".</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-border max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editId ? "Edit" : "Create"} Package</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Package Name *</Label><Input data-testid="combo-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-secondary mt-1" /></div>
            <div>
              <Label className="text-xs">Package Photo</Label>
              <div className="flex items-center gap-3 mt-1">
                {preview ? <img src={preview} alt="" className="h-16 w-24 rounded-md object-cover border border-border" /> : <div className="h-16 w-24 rounded-md bg-secondary border border-border flex items-center justify-center text-muted-foreground"><Package size={20} /></div>}
                <label className="cursor-pointer inline-flex items-center gap-1 text-sm text-primary border border-primary/40 rounded-sm px-3 py-1.5 hover:bg-primary/10">
                  <Upload size={14} /> Upload
                  <input type="file" accept=".jpg,.jpeg,.png,.webp,.gif" className="hidden" onChange={onFile} data-testid="combo-image-input" />
                </label>
              </div>
            </div>
            <div><Label className="text-xs">Short Description</Label><Input data-testid="combo-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-secondary mt-1" /></div>
            <div><Label className="text-xs">Group (public booking)</Label>
              <Select value={form.gender || "women"} onValueChange={(v) => setForm({ ...form, gender: v })}>
                <SelectTrigger className="bg-secondary mt-1" data-testid="combo-gender"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="women">Women Package</SelectItem>
                  <SelectItem value="men">Men Package</SelectItem>
                  <SelectItem value="unisex">Both (Unisex)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Included Services <span className="text-muted-foreground">({form.service_ids.length} selected)</span></Label>
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search services…" className="bg-secondary mt-1 mb-2 h-9" data-testid="combo-service-search" />
              <div className="max-h-48 overflow-y-auto border border-border rounded-md divide-y divide-border" data-testid="combo-service-list">
                {services.filter((s) => { const t = q.toLowerCase(); return !t || (s.name || "").toLowerCase().includes(t) || (s.category || "").toLowerCase().includes(t); }).map((s) => (
                  <label key={s.id} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-secondary/50" data-testid={`combo-svc-${s.id}`}>
                    <input type="checkbox" checked={form.service_ids.includes(s.id)} onChange={() => toggleSvc(s.id)} className="h-4 w-4 accent-[#C9A24B]" />
                    <span className="flex-1 text-sm">{s.name} <span className="text-xs text-muted-foreground">· {s.category}</span></span>
                    <span className="text-sm text-primary">{rupee(s.price)}</span>
                  </label>
                ))}
              </div>
              {form.service_ids.length > 0 && <p className="text-xs text-primary/80 mt-2" data-testid="combo-preview">{form.service_ids.map((id) => services.find((s) => s.id === id)?.name).filter(Boolean).join("  +  ")}</p>}
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">Actual Total</Label><Input value={rupee(actualTotal)} disabled className="bg-secondary/60 mt-1" data-testid="combo-actual-total" /></div>
              <div><Label className="text-xs">Offer Price (₹) *</Label><Input type="number" data-testid="combo-offer-price" value={form.offer_price} onChange={(e) => setForm({ ...form, offer_price: e.target.value })} className="bg-secondary mt-1" /></div>
              <div><Label className="text-xs">Duration (min)</Label><Input type="number" data-testid="combo-duration" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} className="bg-secondary mt-1" /></div>
            </div>
            <div className="flex items-center justify-between rounded-md border border-border bg-secondary/40 px-3 py-2">
              <span className="text-sm">Show in Public Booking</span>
              <button type="button" data-testid="combo-public-toggle" onClick={() => setForm((f) => ({ ...f, show_in_public: !f.show_in_public }))}
                className={`text-xs px-3 py-1 rounded-full border ${form.show_in_public ? "bg-primary text-primary-foreground border-primary" : "bg-secondary text-muted-foreground border-border"}`}>{form.show_in_public ? "ON" : "OFF"}</button>
            </div>
            <div className="flex items-center justify-between rounded-md border border-border bg-secondary/40 px-3 py-2">
              <span className="text-sm">Status</span>
              <button type="button" data-testid="combo-status-toggle" onClick={() => setForm((f) => ({ ...f, status: f.status === "active" ? "inactive" : "active" }))}
                className={`text-xs px-3 py-1 rounded-full border ${form.status === "active" ? "bg-green-500/15 text-green-400 border-green-500/50" : "bg-secondary text-muted-foreground border-border"}`}>{form.status === "active" ? "Active" : "Inactive"}</button>
            </div>
          </div>
          <DialogFooter><Button onClick={save} disabled={busy} data-testid="combo-save" className="rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37]">{busy ? "Saving…" : "Save Changes"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <DialogContent className="bg-card border-border max-w-sm">
          <DialogHeader><DialogTitle className="font-display text-xl">Delete Package</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Are you sure you want to delete "{confirmDel?.name}"? This cannot be undone.</p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmDel(null)}>Cancel</Button>
            <Button className="rounded-sm bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={del} data-testid="combo-delete-confirm">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
